import * as React from 'react';
import { Box, styled } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { FormikValues } from 'formik';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { Option } from 'lib/types';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { isUndefined } from 'lib/typeguards';
import { UberDirectSettingsDisplay } from './DeliveryProvider/UberDirect/UberDirectSettingsDisplay';
import { UberDirectSettingsEdit } from './DeliveryProvider/UberDirect/UberDirectSettingsEdit';

export enum DeliveryProvider {
    UBER_DIRECT = 'UBER_DIRECT'
}

export interface IDeliveryProviderSettingsProps {
    settings?: IPublicAndPrivateSettings;
    locationId?: string;
}

interface IDeliveryProviderComponentConfig {
    display: React.FC<IDeliveryProviderSettingsProps> | null;
    edit: React.FC<IDeliveryProviderSettingsProps> | null;
}

const deliveryProviderComponentConfig: Record<DeliveryProvider, IDeliveryProviderComponentConfig> = {
    [DeliveryProvider.UBER_DIRECT]: { display: UberDirectSettingsDisplay, edit: UberDirectSettingsEdit }
};

const deliveryProviderOptions: Option[] = [
    { label: 'None', value: null },
    { label: 'Uber Direct', value: DeliveryProvider.UBER_DIRECT }
];

const PREFIX = 'DeliverySettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    globalSettings?: IPublicAndPrivateSettings;
    locationId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const DeliverySettings = ({ settings, globalSettings, locationId, onSubmit }: IProps) => {
    const initialValues = React.useMemo(
        () => ({
            deliveryProvider: settings?.deliveryProvider,
            uberDirectCustomerId: settings?.uberDirect?.customerId,
            uberDirectClientId: settings?.uberDirect?.clientId,
            uberDirectClientSecret: settings?.uberDirect?.clientSecret,
            uberDirectWebhookSecret: settings?.uberDirect?.webhookSecret,
            capturePhoneNumberOnOrder_DELIVERY: settings?.capturePhoneNumberOnOrder?.DELIVERY ?? false,
            activeOrderDurationPaddingMins: settings?.delivery?.activeOrderDurationPaddingMins,
            standardPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.standard,
            quietPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.quiet,
            busyPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.busy,
            deliveryBaseFare: settings?.delivery?.baseFare,
            deliveryBaseDistance: settings?.delivery?.baseDistance,
            deliveryCostPerDistance: settings?.delivery?.costPerDistance,
            deliveryDistanceInFullIncrements: settings?.delivery?.distanceInFullIncrements ?? false,
            deliveryDistanceIncrement: settings?.delivery?.distanceIncrement,
            deliveryDistanceUnit: settings?.delivery?.distanceUnit,
            deliveryMinimumBasketAmount: settings?.delivery?.minimumBasketAmount,
            deliveryMinimumBasketAmountFee: settings?.delivery?.minimumBasketAmountFee,
            deliveryChargePercentage: settings?.delivery?.chargePercentage,
            deliveryMaximumChargeValue: settings?.delivery?.maximumChargeValue
        }),
        [settings]
    );

    const deliveryProvider = React.useMemo(
        () => (settings?.deliveryProvider || globalSettings?.deliveryProvider) as DeliveryProvider,
        [settings, globalSettings]
    );

    const DeliveryDisplayComponent = React.useMemo(
        () => deliveryProviderComponentConfig[deliveryProvider]?.display,
        [deliveryProvider]
    );

    const standardPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.standard ??
                'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.standard;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.standard]);

    const quietPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.quiet ?? 'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.quiet;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.quiet]);

    const busyPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.busy ?? 'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.busy;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.busy]);
    const capturePhoneNumberOnOrderDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            const isUnset = isUndefined(settings?.capturePhoneNumberOnOrder?.DELIVERY);
            if (isUnset) {
                return 'Unset (currently uses GLOBAL setting)';
            }
        }
        // Global setting
        return settings?.capturePhoneNumberOnOrder?.DELIVERY ? <Done /> : <Close />;
    }, [locationId, settings?.capturePhoneNumberOnOrder?.DELIVERY]);

    const renderDeliveryEdit = React.useCallback(
        (values: FormikValues) => {
            const editProvider = (values.deliveryProvider ||
                globalSettings?.deliveryProvider) as DeliveryProvider;
            const DeliveryEditComponent = deliveryProviderComponentConfig[editProvider]?.edit;
            return (
                <Box display="flex" flexDirection="column">
                    <SettingDisplay
                        label="Standard Preparation Time"
                        description="The duration is in minutes"
                    >
                        <TextFormField type="number" name="standardPrepTimeMins" />
                    </SettingDisplay>
                    <SettingDisplay label="Quiet Preparation Time" description="The duration is in minutes">
                        <TextFormField type="number" name="quietPrepTimeMins" />
                    </SettingDisplay>
                    <SettingDisplay label="Busy Preparation Time" description="The duration is in minutes">
                        <TextFormField type="number" name="busyPrepTimeMins" />
                    </SettingDisplay>
                    {locationId === undefined && (
                        <SettingDisplay label="Delivery Provider">
                            <SelectFormField
                                name="deliveryProvider"
                                emptyLabel="None"
                                displayEmpty
                                options={deliveryProviderOptions}
                            />
                        </SettingDisplay>
                    )}
                    {DeliveryEditComponent && (
                        <DeliveryEditComponent settings={settings} locationId={locationId} />
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay
                                label="Active Order Duration Padding"
                                description="The duration is in minutes"
                            >
                                <TextFormField type="number" name="activeOrderDurationPaddingMins" />
                            </SettingDisplay>
                            <SettingDisplay label="Base Fare">
                                <TextFormField type="number" name="deliveryBaseFare" />
                            </SettingDisplay>
                            <SettingDisplay label="Base Distance">
                                <TextFormField type="number" name="deliveryBaseDistance" />
                            </SettingDisplay>
                            <SettingDisplay label="Cost Per Distance">
                                <TextFormField type="number" name="deliveryCostPerDistance" />
                            </SettingDisplay>
                            <SettingDisplay label="Distance In Full Increments">
                                <SwitchFormField name="deliveryDistanceInFullIncrements" />
                            </SettingDisplay>
                            <SettingDisplay label="Distance Increments">
                                <TextFormField type="number" name="deliveryDistanceIncrement" />
                            </SettingDisplay>
                            <SettingDisplay label="Distance Unit">
                                <TextFormField name="deliveryDistanceUnit" />
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount">
                                <TextFormField type="number" name="deliveryMinimumBasketAmount" />
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount Fee">
                                <TextFormField type="number" name="deliveryMinimumBasketAmountFee" />
                            </SettingDisplay>
                            <SettingDisplay label="Charge Percentage">
                                <TextFormField type="number" name="deliveryChargePercentage" />
                            </SettingDisplay>
                            <SettingDisplay label="Maximum Charge Value">
                                <TextFormField type="number" name="deliveryMaximumChargeValue" />
                            </SettingDisplay>
                        </>
                    )}
                    {/* Common */}
                    <SettingDisplay label="Capture Phone Number on Delivery">
                        <SwitchFormField name="capturePhoneNumberOnOrder_DELIVERY" />
                    </SettingDisplay>
                </Box>
            );
        },
        [locationId, settings, globalSettings]
    );
    return (
        <StyledMuiCardForm
            title="Delivery"
            initialValues={initialValues}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay
                        label="Standard Preparation Time"
                        description="The duration is in minutes"
                    >
                        {standardPrepTimeDisplay}
                    </SettingDisplay>
                    <SettingDisplay label="Quiet Preparation Time" description="The duration is in minutes">
                        {quietPrepTimeDisplay}
                    </SettingDisplay>
                    <SettingDisplay label="Busy Preparation Time" description="The duration is in minutes">
                        {busyPrepTimeDisplay}
                    </SettingDisplay>
                    {locationId === undefined && (
                        <SettingDisplay label="Delivery Provider">
                            {deliveryProviderOptions.find(
                                option => option.value === settings?.deliveryProvider
                            )?.label ?? 'None'}
                        </SettingDisplay>
                    )}
                    {DeliveryDisplayComponent && (
                        <DeliveryDisplayComponent settings={settings} locationId={locationId} />
                    )}
                    {locationId === undefined && (
                        <>
                            <SettingDisplay
                                label="Active Order Duration Padding"
                                description="The duration is in minutes"
                            >
                                {settings?.delivery?.activeOrderDurationPaddingMins}
                            </SettingDisplay>
                            <SettingDisplay label="Base Fare">{settings?.delivery?.baseFare}</SettingDisplay>
                            <SettingDisplay label="Base Distance">
                                {settings?.delivery?.baseDistance}
                            </SettingDisplay>
                            <SettingDisplay label="Cost Per Distance">
                                {settings?.delivery?.costPerDistance}
                            </SettingDisplay>
                            <SettingDisplay label="Distance In Full Increments">
                                {settings?.delivery?.distanceInFullIncrements ? <Done /> : <Close />}
                            </SettingDisplay>
                            <SettingDisplay label="Distance Increments">
                                {settings?.delivery?.distanceIncrement}
                            </SettingDisplay>
                            <SettingDisplay label="Distance Unit">
                                {settings?.delivery?.distanceUnit}
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount">
                                {settings?.delivery?.minimumBasketAmount}
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount Fee">
                                {settings?.delivery?.minimumBasketAmountFee}
                            </SettingDisplay>
                            <SettingDisplay label="Charge Percentage">
                                {settings?.delivery?.chargePercentage}
                            </SettingDisplay>
                            <SettingDisplay label="Maximum Charge Value">
                                {settings?.delivery?.maximumChargeValue}
                            </SettingDisplay>
                        </>
                    )}
                    {/* Common */}
                    <SettingDisplay label="Capture Phone Number on Delivery">
                        {capturePhoneNumberOnOrderDisplay}
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={renderDeliveryEdit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
